import { Checkbox } from "@/components/ui/checkbox.tsx";
import { cn } from "@/lib/utils.ts";
import type { ReactNode } from "react";

/**
 * One row of an omni picker list: a checkbox on the left as the bullet, the
 * name beside it, and the row's qualifier right-aligned. All the picker cards
 * raised during setup share this grammar so their lists read as one control,
 * whatever each row happens to toggle underneath.
 *
 * The whole row is the click target, following the `CheckboxCard` idiom (a
 * clickable container around a real checkbox) rather than a Button wrapper:
 * the Radix checkbox is itself a button, and nesting it in another button is
 * invalid markup. Mouse clicks land on the container; keyboard and screen
 * readers use the checkbox, which carries the row's label.
 */
export const OmniPickerRow = ({
  checked,
  onToggle,
  disabled,
  locked,
  leading,
  checkboxColor,
  label,
  labelTitle,
  sublabel,
  qualifier,
  ariaContext,
  ariaState,
  dimUnchecked = true,
  statsigName,
  className,
}: {
  checked: boolean;
  onToggle: () => void;
  /** Mid-save guard: blocks a second write without reading as a state. */
  disabled?: boolean;
  /** A row whose state is fixed (a primary calendar): shown checked, not
   *  toggleable, and not dimmed — the check is the truth, not a control. */
  locked?: boolean;
  /** Small adornment between the checkbox and the name (an icon). */
  leading?: ReactNode;
  /** Colors the checkbox itself (outline, and fill when checked) instead of
   *  the theme primary — the calendars card colors each row's checkbox with
   *  its calendar's color, absorbing the color dot into the control. */
  checkboxColor?: string;
  label: string;
  /** Tooltip for the name when the row carries no sublabel to show it. */
  labelTitle?: string;
  sublabel?: string;
  /** Right-aligned annotation: "Default", a schedule, "Flexible". */
  qualifier?: ReactNode;
  /** Extra context appended to the checkbox's accessible name (an account, a
   *  cadence) so rows sharing a visible name stay distinguishable to
   *  assistive tech. */
  ariaContext?: string;
  /** The qualifier's text for assistive tech ("Default", "Flexible",
   *  "Protected", a schedule). The qualifier itself is a node, so its words
   *  reach the accessible name only through this. */
  ariaState?: string;
  /** Fade unchecked rows. Off for lists where unchecked is the resting state
   *  rather than "off" (protected meetings). */
  dimUnchecked?: boolean;
  statsigName: string;
  className?: string;
}) => {
  const inert = disabled || locked;
  return (
    // The click is a convenience mirror of the checkbox, which stays the
    // keyboard and screen-reader control (same shape as CheckboxCard).
    <div
      onClick={(e) => {
        if (inert) return;
        // Inside a <form>, Radix dispatches a bubbling click on its hidden
        // input whenever `checked` changes; toggling on that echo would flip
        // the value straight back (see CheckboxCard).
        if (e.target instanceof HTMLInputElement) return;
        onToggle();
      }}
      className={cn(
        "flex w-full items-center gap-2.5 px-3 py-2 text-left",
        !inert && "cursor-pointer hover:bg-muted/50",
        className,
      )}
      data-statsig-name={statsigName}
    >
      <Checkbox
        checked={checked}
        disabled={inert}
        onCheckedChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        // The accessible name carries the row's disambiguating context and
        // its state words — "Holidays in United States" appears once per
        // account, and "Flexible"/"Protected" is meaning the bare checked
        // bit doesn't convey.
        aria-label={[label, sublabel, ariaContext, ariaState].filter(Boolean).join(", ")}
        // A locked row's check is a fact, not a disabled control, so it keeps
        // full contrast.
        className={cn(locked && "disabled:opacity-100")}
        style={
          checkboxColor
            ? { borderColor: checkboxColor, ...(checked ? { backgroundColor: checkboxColor } : {}) }
            : undefined
        }
      />
      {leading}
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-sm",
            dimUnchecked && !checked && !locked && "opacity-60",
          )}
          title={labelTitle ?? sublabel ?? undefined}
        >
          {label}
        </span>
        {sublabel && (
          <span className="block truncate text-xs text-muted-foreground">{sublabel}</span>
        )}
      </span>
      {/* Capped rather than free: a wordy qualifier truncates and gives the
          name its room back instead of squeezing it to an ellipsis. */}
      {qualifier && (
        <span className="max-w-[40%] shrink-0 truncate text-xs text-muted-foreground">
          {qualifier}
        </span>
      )}
    </div>
  );
};
